/* eslint-disable */
import React from 'react';
import { makeStyles, Typography } from '@rentacenter/racstrap';
import { ReactComponent as ProcessSuccess } from '../../assets/images/validation-success.svg';

export interface FundingApprovedPanelProps {
  approvalLimitAmount: string;
  weeklyRate: string;
  totalCost: string;
  vendorName: string;
}

const FONT_CHOICE = 'OpenSans-semibold';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: '0.5rem 0.75rem',
    overflowY: 'auto' as const,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  successIcon: {
    width: '28px',
    height: '28px',
  },
  title: {
    fontSize: '0.875rem',
    fontFamily: FONT_CHOICE,
    fontWeight: 700,
    textAlign: 'center' as const,
    marginBottom: '0.25rem',
    color: '#111111',
  },
  subtitle: {
    fontSize: '0.7rem',
    fontFamily: 'OpenSans-regular',
    textAlign: 'center' as const,
    color: '#333333',
    marginBottom: '0.5rem',
    lineHeight: 1.3,
  },
  transactionCard: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E8F1FE',
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    width: '100%',
    marginBottom: '0.375rem',
  },
  transactionColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    flex: 1,
  },
  divider: {
    width: '2px',
    backgroundColor: '#B0C4DE',
    alignSelf: 'stretch',
    margin: '0 0.5rem',
  },
  transactionLabel: {
    fontSize: '0.65rem',
    fontFamily: FONT_CHOICE,
    color: '#333333',
  },
  transactionAmount: {
    fontSize: '0.875rem',
    fontFamily: FONT_CHOICE,
    fontWeight: 700,
    color: '#111111',
  },
  taxLabel: {
    fontSize: '0.6rem',
    fontFamily: 'OpenSans-regular',
    color: '#666666',
  },
  taxNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.375rem',
    width: '100%',
    marginTop: '0.375rem',
    marginBottom: '0.5rem',
    padding: '0 0.25rem',
  },
  taxNoteIcon: {
    fontSize: '0.75rem',
    color: '#1976D2',
    flexShrink: 0,
    marginTop: '2px',
  },
  taxNoteText: {
    fontSize: '0.65rem',
    fontFamily: 'OpenSans-regular',
    color: '#555555',
    lineHeight: 1.3,
  },
  checkoutButton: {
    textTransform: 'none' as const,
    fontFamily: FONT_CHOICE,
    fontSize: '0.8rem',
    padding: '0.375rem 1.5rem',
    borderRadius: '4px',
  },
  errorMessage: {
    fontSize: '0.65rem',
    color: '#C62828',
    textAlign: 'center' as const,
    marginTop: '0.375rem',
  },
}));

/**
 * Formats a numeric string as US currency (e.g., "1234.56" → "$1,234.56").
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return `$${value}`;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

/**
 * FundingApprovedPanel — renders the "Approval Successful" confirmation on the Left_Panel
 * after funding is approved. Shows vendor name, transaction amount with +tax label,
 * tax-exempt documentation note, and "Continue Checkout" button to open retailer cart popup.
 *
 * Requirements: 2.1, 2.2, 2.3
 */
export const FundingApprovedPanel: React.FC<FundingApprovedPanelProps> = ({
  approvalLimitAmount,
  weeklyRate,
  totalCost,
  vendorName,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {/* Green checkmark icon */}
      <div className={classes.iconContainer}>
        <ProcessSuccess className={classes.successIcon} />
      </div>

      {/* "Approval Successful!" title */}
      <Typography className={classes.title}>Approval Successful!</Typography>

      {/* Subtitle with vendor name */}
      <Typography className={classes.subtitle}>
        Your prefund request has been approved.
        <br />
        Please proceed to complete your checkout at {vendorName}.
      </Typography>

      {/* Transaction Amount and Weekly Payment side by side */}
      <div className={classes.transactionCard}>
        <div className={classes.transactionColumn}>
          <Typography className={classes.transactionLabel}>
            Transaction Amount
          </Typography>
          <Typography className={classes.transactionAmount}>
            {formatCurrency(approvalLimitAmount)}
          </Typography>
          <Typography className={classes.taxLabel}>{'( +tax )'}</Typography>
        </div>
        <div className={classes.divider} />
        <div className={classes.transactionColumn}>
          <Typography className={classes.transactionLabel}>
            Estimated Weekly Payment
          </Typography>
          <Typography className={classes.transactionAmount}>
            {formatCurrency(weeklyRate)}
          </Typography>
          <Typography className={classes.taxLabel}>{'( +tax )'}</Typography>
        </div>
      </div>

      {/* Tax-exempt documentation note */}
      <div className={classes.taxNote}>
        <span className={classes.taxNoteIcon}>ℹ</span>
        <Typography className={classes.taxNoteText}>
          Please note: You will be required to upload tax-exempt documentation during the checkout process.
        </Typography>
      </div>
    </div>
  );
};

export default FundingApprovedPanel;
